import {useCallback, useState} from 'react';
import ArrIconButton from '../../../common/arr-icon-button/ArrIconButton';
import ArrTransferList, {
  KeyValue,
} from '../../../common/arr-transfer-list/ArrTransferList';
import {transition_time_default} from '../../../../shared/constants';
import {ArrDrawer} from '../../../common/arr-drawer/ArrDrawer';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import {t} from 'i18next';
import './Metadata.scss';
import {Typography} from '@mui/material';
import ArrButton from '../../../common/arr-button/ArrButton';
import {IMetadataItem} from '../../../../shared/interfaces';
import {differenceBy, union} from 'lodash';
import {useDispatch, useSelector} from 'react-redux';
import {RootState} from '../../../../redux/store';
import {
  setAssignedMetadata,
  setUnassignedMetadata,
} from '../../../../redux/slice/assignmentSlice';

interface IMetadata {
  openDrawer: boolean;
  setOpenMetadataDrawer: (open: boolean) => void;
}

const Metadata = ({openDrawer, setOpenMetadataDrawer}: IMetadata) => {
  const dispatch = useDispatch();
  const listData = useSelector((state: RootState) => state.assignment.listData);
  const {isCascadeEnabled, parentAssignmentGroupID} = useSelector(
    (state: RootState) => state.assignment.formData.assignmentModel
  );
  const treeData = useSelector(
    (state: RootState) => state.assignment.assignmentTree
  );
  const isParentCascaseEnabled = treeData?.find(
    (item) => +item.id === parentAssignmentGroupID
  )?.li_attr.isCascadeEnabled;
  const [selectedAssignedList, setSelectedAssignedList] = useState<
    IMetadataItem[]
  >([]);

  const [availableAssignedList, setAvailableAssignedList] = useState<
    IMetadataItem[]
  >(listData.assignedMetadata);
  const [selectedUnassignedList, setSelectedUnassignedList] = useState<
    IMetadataItem[]
  >([]);

  const [availableUnassignedList, setAvailableUnassignedList] = useState<
    IMetadataItem[]
  >(listData.unAssignedMetadata);
  const handleCancel = () => {
    setOpenMetadataDrawer(false);
  };

  const handleSave = () => {
    dispatch(setAssignedMetadata(availableAssignedList));
    dispatch(setUnassignedMetadata(availableUnassignedList));
    setOpenMetadataDrawer(false);
  };
  const handleUnassignChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    user: KeyValue
  ) => {
    const isChecked = event.target.checked;

    setSelectedUnassignedList((prev) => {
      if (isChecked) {
        const alreadyExists = prev.some((u) => u.key === user.id);
        return alreadyExists
          ? prev
          : [...prev, {key: user.id, text: user.label, isSelected: false}];
      } else {
        return prev.filter((u) => u.key !== user.id);
      }
    });
  };
  const handleAssignChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    user: KeyValue
  ) => {
    const isChecked = event.target.checked;

    setSelectedAssignedList((prev) => {
      if (isChecked) {
        const alreadyExists = prev.some((u) => u.key === user.id);
        return alreadyExists
          ? prev
          : [...prev, {key: user.id, text: user.label, isSelected: false}];
      } else {
        return prev.filter((u) => u.key !== user.id);
      }
    });
  };
  const moveToAssigned = useCallback(() => {
    const updatedSelectedFields: IMetadataItem[] = union(
      availableAssignedList,
      selectedUnassignedList
    );

    const updatedUnassigned = differenceBy(
      availableUnassignedList,
      selectedUnassignedList,
      'key'
    );

    setSelectedUnassignedList([]);
    setAvailableAssignedList(updatedSelectedFields);
    setAvailableUnassignedList(updatedUnassigned);
  }, [selectedUnassignedList, availableAssignedList]);
  const moveToUnassigned = useCallback(() => {
    const updatedSelectedFields = union(
      availableUnassignedList,
      selectedAssignedList
    );
    const updatedAssigned = differenceBy(
      availableAssignedList,
      selectedAssignedList,
      'text'
    );
    setSelectedAssignedList([]);
    setAvailableUnassignedList(updatedSelectedFields);
    setAvailableAssignedList(updatedAssigned);
  }, [availableUnassignedList, selectedAssignedList]);
  return (
    <ArrDrawer
      transition={transition_time_default}
      showHeader={false}
      showButton={false}
      scrollable
      isOpen={openDrawer}
      size="large"
      side="right"
      onClose={() => setOpenMetadataDrawer(false)}
    >
      <div
        className="metadata-container"
        data-testid={`metadata-title-cancel-save`}
      >
        <div
          className="metadata-action"
          data-testid={`metadata-title-cancel-save-btn`}
        >
          <div className="metadata-label" data-testid={`metadata-title`}>
            <Typography
              className="typo-text headline3_size metadata-title"
              data-testid={`metadata-title-text`}
            >
              {t('assignment.new.metadataFlow.title')}
            </Typography>
          </div>
          <ArrButton
            variant="secondary"
            size="small"
            onClick={handleCancel}
            dataTestId="metadata-cancel-button"
            className="cancel-button"
            name={t('common.cancel')}
            id={t('common.cancel')}
          >
            {t('common.cancel')}
          </ArrButton>
          <ArrButton
            variant="primary"
            size="small"
            onClick={handleSave}
            dataTestId="save-button"
            className="save-button"
            name={t('common.save')}
            id={t('common.save')}
            disabled={
              availableAssignedList?.length === 0 || isParentCascaseEnabled
            }
          >
            {t('common.save')}
          </ArrButton>
        </div>
        <div className="metadata-list-container" data-testid={`metadata-div`}>
          <ArrTransferList
            showCheckbox={true}
            selectedList={selectedUnassignedList.map((item) => ({
              id: Number(item.key),
              label: item.text ?? '',
            }))}
            displayedList={availableUnassignedList.map((item) => ({
              id: Number(item.key),
              label: item.text ?? '',
            }))}
            onChange={handleUnassignChange}
            checkboxDataTestId="assignment-metadata-unassigned-transfer-list-checkbox"
            title={t('assignment.new.metadataFlow.unassignedMetadata')}
            listItemContainerClass="metadata-list"
            disable={isParentCascaseEnabled}
          />
          <div
            className={`transfer-action-container`}
            data-testid={`metadata-div-btn`}
          >
            <ArrIconButton
              ariaLabel="forward-icon"
              onClick={moveToAssigned}
              size="small"
              className="tranfer-action-icon-button"
              icon={<ArrowForwardIcon className="arrow-icon" />}
              disabled={selectedUnassignedList?.length === 0}
              testId="forward-icon"
              id="forward-icon"
              name="forward-icon"
            />
            <ArrIconButton
              ariaLabel="back-icon"
              onClick={moveToUnassigned}
              disabled={selectedAssignedList?.length === 0}
              size="small"
              className="tranfer-action-icon-button"
              icon={<ArrowBackIcon className="arrow-icon" />}
              testId="back-icon"
              id="back-icon"
              name="back-icon"
            />
          </div>
          <ArrTransferList
            showCheckbox={true}
            selectedList={selectedAssignedList.map((item) => ({
              id: Number(item.key),
              label: item.text ?? '',
            }))}
            displayedList={availableAssignedList.map((item) => ({
              id: Number(item.key),
              label: item.text,
            }))}
            onChange={handleAssignChange}
            checkboxDataTestId="assignment-metadata-assigned-transfer-list-checkbox"
            title={t('assignment.new.metadataFlow.assignedMetadata')}
            listItemContainerClass="metadata-list"
            disable={isParentCascaseEnabled}
          />
        </div>
      </div>
    </ArrDrawer>
  );
};

export default Metadata;
